/**
 * Duty Control Catalog — V85-0-17 / ADR-0034.
 *
 * Maps seeded duty names from jurisdiction rules' `required_controls`
 * to baseline control specifications. These are the executable
 * specifications the GaC compiler needs to produce policy bundles.
 *
 * The catalog is the fallback when no workspace-specific `control_specs`
 * row exists for a control ID. Workspace admins can override any entry
 * by creating a spec in the DB (via ControlSpecService.createSpec).
 *
 * Categories:
 * - `disclosure`: Evidence = attestation that disclosure was made.
 *   No automated numeric gate; the obligation is to disclose, not
 *   to measure. The OPA bundle checks evidence existence + status.
 * - `documentation`: Evidence = document/report exists and is current.
 * - `assessment`: Evidence = completed assessment/audit report.
 * - `governance`: Evidence = organizational body/role is designated.
 *
 * Every duty that appears in a seeded rule's `obligation_json.required_controls`
 * MUST have an entry here. If a duty is missing, the compiler logs it as
 * unresolved and the bundle omits it — this is intentional so that new
 * duties added to rules via the editorial queue don't silently compile
 * to nothing, but the catalog can be extended to cover them.
 */
import { vocabularyDutyControls } from './vocabulary-extension-service.js';
// ============================================================
// Static catalog
// ============================================================
const entries = [
    // ── Disclosure duties ──────────────────────────────────────
    ['ai_interaction_disclosure', {
            control_id: 'ai_interaction_disclosure',
            title: 'AI Interaction Disclosure',
            category: 'disclosure',
            accepted_signals: ['notice_template', 'interaction_disclosure_record', 'healthcare_disclosure_record'],
            threshold_default: {},
            freshness_max_age_seconds: null,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['synthetic_content_marking', {
            control_id: 'synthetic_content_marking',
            title: 'Synthetic Content Marking',
            category: 'disclosure',
            accepted_signals: ['synthetic_marking_evidence', 'content_labeling_record'],
            threshold_default: {},
            freshness_max_age_seconds: null,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['consumer_notice', {
            control_id: 'consumer_notice',
            title: 'Consumer Notice',
            category: 'disclosure',
            accepted_signals: ['notice_template', 'consumer_notification_record'],
            threshold_default: {},
            freshness_max_age_seconds: null,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['adverse_decision_disclosure', {
            control_id: 'adverse_decision_disclosure',
            title: 'Adverse Decision Disclosure',
            category: 'disclosure',
            accepted_signals: ['adverse_disclosure_template', 'adverse_decision_record'],
            threshold_default: {},
            freshness_max_age_seconds: null,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['candidate_notice', {
            control_id: 'candidate_notice',
            title: 'Candidate Notice',
            category: 'disclosure',
            accepted_signals: ['candidate_notice_record', 'notice_template'],
            threshold_default: {},
            freshness_max_age_seconds: null,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['decision_explanation', {
            control_id: 'decision_explanation',
            title: 'Decision Explanation',
            category: 'disclosure',
            accepted_signals: ['explanation_documentation', 'decision_rationale_record'],
            threshold_default: {},
            freshness_max_age_seconds: null,
            no_data_behaviour: 'insufficient_evidence',
        }],
    // ── Documentation duties ───────────────────────────────────
    ['technical_documentation', {
            control_id: 'technical_documentation',
            title: 'Technical Documentation',
            category: 'documentation',
            accepted_signals: ['annex_iv_documentation', 'technical_documentation_package'],
            threshold_default: {},
            freshness_max_age_seconds: 365 * 24 * 3600, // annual review
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['developer_documentation', {
            control_id: 'developer_documentation',
            title: 'Developer Documentation',
            category: 'documentation',
            accepted_signals: ['developer_documentation_package', 'developer_docs'],
            threshold_default: {},
            freshness_max_age_seconds: 365 * 24 * 3600,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['deployer_duties', {
            control_id: 'deployer_duties',
            title: 'Deployer Compliance Documentation',
            category: 'documentation',
            accepted_signals: ['deployer_instructions', 'deployer_compliance_record'],
            threshold_default: {},
            freshness_max_age_seconds: 365 * 24 * 3600,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['risk_management_system', {
            control_id: 'risk_management_system',
            title: 'Risk Management System',
            category: 'documentation',
            accepted_signals: ['risk_management_plan', 'risk_treatment_plan'],
            threshold_default: {},
            freshness_max_age_seconds: 365 * 24 * 3600,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['minimum_risk_practices', {
            control_id: 'minimum_risk_practices',
            title: 'Minimum Risk Practices',
            category: 'documentation',
            accepted_signals: ['risk_practice_documentation', 'minimum_risk_record'],
            threshold_default: {},
            freshness_max_age_seconds: 365 * 24 * 3600,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['record_retention', {
            control_id: 'record_retention',
            title: 'Record Retention',
            category: 'documentation',
            accepted_signals: ['employment_records', 'retention_policy_record'],
            threshold_default: {},
            freshness_max_age_seconds: null,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['opt_out_mechanism', {
            control_id: 'opt_out_mechanism',
            title: 'Opt-Out Mechanism',
            category: 'documentation',
            accepted_signals: ['opt_out_process_documentation', 'opt_out_record'],
            threshold_default: {},
            freshness_max_age_seconds: null,
            no_data_behaviour: 'insufficient_evidence',
        }],
    // ── Assessment duties ──────────────────────────────────────
    ['impact_assessment', {
            control_id: 'impact_assessment',
            title: 'Impact Assessment',
            category: 'assessment',
            accepted_signals: ['impact_assessment_report', 'algorithmic_impact_assessment'],
            threshold_default: {},
            freshness_max_age_seconds: 365 * 24 * 3600,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['bias_audit', {
            control_id: 'bias_audit',
            title: 'Bias Audit',
            category: 'assessment',
            accepted_signals: ['bias_audit_report', 'anti_bias_testing_report'],
            threshold_default: {},
            freshness_max_age_seconds: 365 * 24 * 3600,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['conformity_assessment', {
            control_id: 'conformity_assessment',
            title: 'Conformity Assessment',
            category: 'assessment',
            accepted_signals: ['conformity_certificate', 'conformity_assessment_report'],
            threshold_default: {},
            freshness_max_age_seconds: 365 * 24 * 3600,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['red_team_testing', {
            control_id: 'red_team_testing',
            title: 'Red Team Testing',
            category: 'assessment',
            accepted_signals: ['red_team_report', 'adversarial_testing_report'],
            threshold_default: {},
            freshness_max_age_seconds: 180 * 24 * 3600, // semi-annual
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['anti_discrimination_safeguards', {
            control_id: 'anti_discrimination_safeguards',
            title: 'Anti-Discrimination Safeguards',
            category: 'assessment',
            accepted_signals: ['safeguard_documentation', 'anti_discrimination_report'],
            threshold_default: {},
            freshness_max_age_seconds: 365 * 24 * 3600,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['risk_mapping', {
            control_id: 'risk_mapping',
            title: 'Risk Mapping',
            category: 'assessment',
            accepted_signals: ['risk_map_document', 'risk_register'],
            threshold_default: {},
            freshness_max_age_seconds: 365 * 24 * 3600,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['risk_measurement', {
            control_id: 'risk_measurement',
            title: 'Risk Measurement',
            category: 'assessment',
            accepted_signals: ['measurement_methodology', 'risk_metrics_report'],
            threshold_default: {},
            freshness_max_age_seconds: 365 * 24 * 3600,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['publish_audit_summary', {
            control_id: 'publish_audit_summary',
            title: 'Published Audit Summary',
            category: 'assessment',
            accepted_signals: ['published_audit_summary', 'audit_summary_publication'],
            threshold_default: {},
            freshness_max_age_seconds: 365 * 24 * 3600,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['human_oversight', {
            control_id: 'human_oversight',
            title: 'Human Oversight',
            category: 'assessment',
            accepted_signals: ['human_oversight_design_doc', 'appeal_process_documentation', 'oversight_plan'],
            threshold_default: {},
            freshness_max_age_seconds: 365 * 24 * 3600,
            no_data_behaviour: 'insufficient_evidence',
        }],
    // ── Governance duties ──────────────────────────────────────
    ['governance_body', {
            control_id: 'governance_body',
            title: 'Governance Body',
            category: 'governance',
            accepted_signals: ['governance_charter', 'caio_designation', 'governance_body_record'],
            threshold_default: {},
            freshness_max_age_seconds: null,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['responsible_official', {
            control_id: 'responsible_official',
            title: 'Responsible Official',
            category: 'governance',
            accepted_signals: ['responsible_official_designation', 'official_appointment_record'],
            threshold_default: {},
            freshness_max_age_seconds: null,
            no_data_behaviour: 'insufficient_evidence',
        }],
    ['use_case_inventory', {
            control_id: 'use_case_inventory',
            title: 'Use Case Inventory',
            category: 'governance',
            accepted_signals: ['inventory_submission', 'use_case_register'],
            threshold_default: {},
            freshness_max_age_seconds: 365 * 24 * 3600,
            no_data_behaviour: 'insufficient_evidence',
        }],
    // ── GDPR (vocabulary 2026.10; migration 790 rules) ─────────
    ...gdprAndOmb([
        ['gdpr_compliance', 'GDPR Compliance Programme (Art. 3, 30)', 'governance', ['records_of_processing', 'data_protection_assessment', 'privacy_controls_documentation'], 365],
        ['eu_representative', 'EU Representative (Art. 27)', 'governance', ['eu_representative_designation'], null],
        ['uk_gdpr_compliance', 'UK GDPR Compliance Programme', 'governance', ['records_of_processing', 'data_protection_assessment', 'privacy_controls_documentation'], 365],
        ['uk_representative', 'UK Representative (UK GDPR Art. 27)', 'governance', ['uk_representative_designation'], null],
        ['human_intervention', 'Human Intervention in Automated Decisions (Art. 22(3))', 'documentation', ['human_review_procedure', 'human_oversight_plan', 'escalation_path_documentation'], null],
        ['appeal_process', 'Contest / Appeal an Automated Decision', 'documentation', ['contest_process', 'appeal_process_documentation'], null],
        ['express_point_of_view', 'Right to Express a Point of View (Art. 22(3))', 'documentation', ['data_subject_rights_process', 'contest_process'], null],
        ['processing_description', 'Description of Processing (Art. 35(7)(a))', 'documentation', ['dpia_report', 'records_of_processing'], 365],
        ['necessity_assessment', 'Necessity and Proportionality (Art. 35(7)(b))', 'assessment', ['dpia_report', 'data_protection_assessment'], 365],
        ['rights_risk_assessment', 'Risks to Data Subjects (Art. 35(7)(c))', 'assessment', ['dpia_report', 'privacy_impact_assessment', 'data_protection_assessment'], 365],
        ['risk_mitigation', 'Measures to Address Risks (Art. 35(7)(d))', 'documentation', ['risk_treatment_plan', 'dpia_report'], 365],
        // Art. 36 applies only when the DPIA leaves a high residual risk: a documented acceptance of a non-high residual
        // risk satisfies it as well as a consultation record.
        ['prior_consultation', 'Prior Consultation with the Supervisory Authority (Art. 36)', 'governance', ['supervisory_authority_consultation', 'residual_risk_acceptance'], null],
        ['special_category_condition', 'Art. 9(2) Condition for Special-Category Data', 'documentation', ['art9_condition_record', 'consent_record'], null],
        ['special_category_safeguards', 'Special-Category Data Safeguards (Art. 9)', 'documentation', ['safeguard_documentation', 'access_matrix', 'data_minimization_evidence'], null],
        // ── OMB M-25-21 / M-25-22 / M-26-04 (vocabulary 2026.10; migration 770 rules) ──
        ['operator_training', 'Operator Training for High-Impact AI (M-25-21)', 'documentation', ['operator_training_record', 'training_plan', 'competence_assessment'], 365],
        ['public_feedback', 'Public Feedback Channel (M-25-21)', 'disclosure', ['feedback_channel', 'stakeholder_engagement_record'], null],
        ['vendor_evaluation_access', 'Agency Evaluation Access (M-25-22)', 'documentation', ['evaluation_access_terms'], null],
        ['data_use_restriction', 'Government Data Use Restriction (M-25-22)', 'documentation', ['data_use_terms', 'data_handling_policy'], null],
        ['vendor_lock_in_protection', 'Vendor Lock-in Protection (M-25-22)', 'documentation', ['portability_plan'], null],
        ['change_notification', 'Notice of New AI Features (M-25-22)', 'disclosure', ['change_notification_process', 'change_record'], null],
        ['unbiased_ai_principles', 'Unbiased AI Principles (M-26-04)', 'assessment', ['bias_evaluation_report', 'model_card', 'acceptable_use_policy'], 365],
        ['llm_transparency_disclosure', 'LLM Transparency Minimum (M-26-04)', 'disclosure', ['acceptable_use_policy', 'model_card', 'end_user_resources', 'output_feedback_channel'], null],
        // ── EU AI Act after the Digital Omnibus (vocabulary 2026.11; v11.6 V116-3-4, migrations 966+) ──
        ['accuracy_testing', 'Accuracy Testing (EU AI Act Art. 15(1)-(3))', 'assessment', ['accuracy_evaluation', 'validation_test_report', 'performance_metrics_report'], 365],
        ['ai_cybersecurity', 'AI-Specific Cybersecurity (Art. 15(5), Art. 55(1)(d))', 'assessment', ['adversarial_robustness_report', 'data_poisoning_detection_config', 'prompt_injection_defense_config', 'penetration_test_report'], 365],
        ['copyright_policy', 'Copyright Policy incl. TDM Opt-outs (Art. 53(1)(c))', 'documentation', ['ip_policy', 'copyright_filter_config', 'training_data_license_audit'], 365],
        ['data_governance', 'Training, Validation and Test Data Governance (Art. 10)', 'documentation', ['data_governance_policy', 'dataset_card', 'data_quality_report', 'representativeness_assessment'], 365],
        ['deepfake_disclosure', 'Deepfake and AI-Generated Text Disclosure (Art. 50(4))', 'disclosure', ['synthetic_media_disclosure', 'content_labeling_record'], null],
        ['eu_database_registration', 'EU Database Registration (Art. 49)', 'governance', ['eu_database_registration_record'], null],
        ['generative_abuse_safeguards', 'Safeguards Against Intimate-Image and CSAM Generation (Art. 5(1a))', 'assessment', ['harmful_content_filter_config', 'safety_filter_rules', 'red_team_report'], 365],
        ['high_risk_classification_assessment', 'High-Risk Classification Assessment (Art. 6(3)-(4))', 'assessment', ['tier_assessment'], null],
        ['quality_management_system', 'Quality Management System (Art. 17)', 'documentation', ['qms_manual', 'document_control_procedure', 'management_review_report'], 365],
    ]),
];
/** Compact rows for the 2026.10 / 2026.11 additions: [duty, title, category, accepted signals, freshness in days or null]. */
function gdprAndOmb(rows) {
    return rows.map(([duty, title, category, accepted_signals, days]) => [duty, {
            control_id: duty,
            title,
            category,
            accepted_signals,
            threshold_default: {},
            freshness_max_age_seconds: days === null ? null : days * 24 * 3600,
            no_data_behaviour: 'insufficient_evidence',
        }]);
}
export const DUTY_CATALOG = new Map(entries);
// ============================================================
// Catalog → ControlSpec conversion
// ============================================================
/**
 * Convert a catalog entry to a `ControlSpec`-shaped object suitable for
 * the compiler. The spec is synthetic (no DB row) but has the same shape
 * the compiler expects. `spec_hash` is deterministic from the entry so
 * two calls with the same catalog produce the same hash.
 */
export function catalogEntryToSpec(entry) {
    const applicabilityJson = JSON.stringify({});
    const signalsJson = JSON.stringify(entry.accepted_signals.map(s => ({ kind: s })));
    const predicateJson = JSON.stringify({});
    const thresholdJson = JSON.stringify(entry.threshold_default);
    const frameworkJson = JSON.stringify({});
    const sufficiencyJson = JSON.stringify({});
    const changelogJson = JSON.stringify([`Baseline from duty catalog v1 (${entry.category})`]);
    return {
        spec_id: `catalog-${entry.control_id}`,
        control_id: entry.control_id,
        spec_version: '1.0.0-catalog',
        spec_hash: `catalog-${entry.control_id}-v1`,
        applicability_json: applicabilityJson,
        scope_selector: null,
        accepted_signals_json: signalsJson,
        predicate_json: predicateJson,
        threshold_default_json: thresholdJson,
        framework_overrides_json: frameworkJson,
        freshness_max_age_seconds: entry.freshness_max_age_seconds,
        sufficiency_json: sufficiencyJson,
        verdict_policy: 'all_must_pass',
        no_data_behaviour: entry.no_data_behaviour,
        approved_by: 'catalog',
        approved_at: '2026-09-20T00:00:00.000Z',
        changelog_json: changelogJson,
        created_at: '2026-09-20T00:00:00.000Z',
        superseded_at: null,
    };
}
/**
 * Resolve a control spec: first from the DB-stored specs map, then
 * falling back to the duty catalog. Returns null only if neither source
 * has the control.
 */
export function resolveControlSpec(controlId, dbSpecs) {
    const fromDb = dbSpecs.get(controlId);
    if (fromDb)
        return fromDb;
    const catalogEntry = DUTY_CATALOG.get(controlId);
    if (catalogEntry)
        return catalogEntryToSpec(catalogEntry);
    return null;
}
/**
 * V85-1-9g: Resolve a duty to its control_ids.
 *
 * Combines two sources:
 *   1. The `controls` table (duty column, migration 134)
 *   2. The static duty catalog (where control_id = duty name)
 *
 * Returns a deduplicated list of control_ids. An empty array means the
 * duty has no implementing controls — the caller should treat this as
 * an error (forces content coverage before rules can activate).
 */
export async function dutyToControlIds(db, duty) {
    const ids = new Set();
    // Source 1: DB controls tagged with this duty.
    const dbResult = await db.query('SELECT control_id FROM controls WHERE duty = ?', [duty]);
    for (const row of dbResult.results) {
        ids.add(row.control_id);
    }
    // Source 2: The static catalog uses duty as control_id.
    if (DUTY_CATALOG.has(duty)) {
        ids.add(duty);
    }
    // Source 3 (V112-5-2 / ADR-0116): a duty an editor added through the review queue names its control.
    for (const id of await vocabularyDutyControls(db, duty))
        ids.add(id);
    return [...ids];
}
/**
 * V85-1-9g: Batch-resolve all duties to control_ids.
 * Used by obligation-set assembly to populate required_controls[].
 */
export async function batchDutyToControlIds(db, duties) {
    const result = new Map();
    for (const duty of duties) {
        result.set(duty, await dutyToControlIds(db, duty));
    }
    return result;
}
//# sourceMappingURL=duty-control-catalog.js.map